import { z } from "zod";

export interface FundExplorerApiData {
    metadata: MetaData;
    funds: Fund[];
}

export interface Fund {
    data: Array<RawValue>;
    share_classes: ShareClass[];
    id: number;
    primary_shareclass: number | null;
    currency: Currency;
}

export enum Currency {
    CAD = "CAD",
    Chf = "CHF",
    Eur = "EUR",
    Gbp = "GBP",
    Jpy = "JPY",
    Usd = "USD",
}

export interface ShareClass {
    data: Array<RawValue>;
    id: number;
    currency: Currency;
    listings?: Listing[];
}

export type Listing = Omit<ShareClass, "listings">;

export interface MetaData {
    fund_fields: FieldMeta[];
    share_class_fields: FieldMeta[];
    listing_fields: FieldMeta[];
    as_at_date: Date;
    performance_end_date: Date;
    quarter_end_date: Date;
    year_end_date: Date;
    featured_funds: number[] | [];
    audience_name: string;
}

export interface FieldMeta {
    code_name: string;
    label: string;
    format: Format | null;
    null_replacement: NullReplacement;
    type: Type;
    currency?: null | string;
    format_rules?: FormatRule[] | null;
}
export type MetaDataProp = keyof MetaData;
export type MetaDataFieldProp = keyof Pick<
    MetaData,
    "fund_fields" | "share_class_fields" | "listing_fields"
>;
export type MetaValue = FieldMeta[] | Date | string | number[] | [];
export type FieldMetaProp = keyof FieldMeta;

export type NestedChildProp = "share_classes" | "listings" | "none";

export type Format = string;

export interface FormatRule {
    min_value: string;
    formats: Formats;
}

export type Formats = {
    [key in Currency]?: Format;
};

export enum NullReplacement {
    Empty = "-",
}

export enum Type {
    Boolean = "boolean",
    Currency = "currency",
    Date = "date",
    Document = "document",
    HTML = "html",
    Number = "number",
    String = "string",
}
export type DocURL = string;
export type DocName = string;
export type DocLang = string;
export type DocDate = string;
export type RawDocumentValue = [DocURL, DocName, DocLang, DocDate];
export type RawValue = unknown;

export type FundData = Data<Fund> & {
    share_classes: number[] | [];
};

export type ShareClassData = Data<ShareClass> & {
    listings: number[] | [];
};

export type ListingData = Listing & Data<Listing>;

export type TopLevelDataObj = FundData | ShareClassData | ListingData;
export type TopLevelDataType = "fund" | "shareclass" | "listing";
export type TopLevelObjWithParentId = TopLevelObj & {
    parentId: number;
};

export type Data<T> = Omit<T, "data" | "share_classes" | "listings"> & {
    type: TopLevelDataType;
    data: Record<FieldMeta["code_name"], FormattedValue>;
    parentId?: string;
    localId: string;
};

export type FundExplorerDataIndexed = Record<string, TopLevelDataObj>;

export type TopLevelObj = (Fund | ShareClass | Listing) & {
    parentId?: number;
};

export const docObjSchema = z.object({
    name: z.string(),
    lang: z.string(),
    url: z.string(),
    asAtDate: z.string().optional(),
});

export const docArrSchema = z.array(docObjSchema);

export type DocObj = z.infer<typeof docObjSchema>;

export type DocObjArr = z.infer<typeof docArrSchema>;

export function isDocObjArr(a: FormattedValue | undefined): a is DocObj[] {
    return a && Array.isArray(a) && a[0] ? true : false;
}
export type FormattedValue = string | boolean | DocObj[];

export type FormatFn<P = string> = (
    meta: FieldMeta,
    rawValue: P,
    rawObj?: TopLevelObj,
) => string | DocObj[];

export type ShareClassMeta = {
    shareclassPageURL: string;
    shortName: string;
    isin: string;
    sedol: string;
    bloomberg: string;
    cusip: string;
    citi: string;
    wkn: string;
    accInc: string;
    ocf: string;
    mex: string;
    launchDateGerman: string;
    performanceBasis: string;
    benchmarkBloomberg: string;
    benchmarkShareClass: string;
    launchDate: string;
    cum1m: string;
    cum3m: string;
    cum6m: string;
    cum1y: string;
    cum3y: string;
    cum5y: string;
    ann3y: string;
    ann5y: string;
    cumYtd: string;
    factSheet:
        | {
              lang: string;
              name: string;
              url: string;
          }[]
        | "-";
    kiid:
        | {
              lang: string;
              name: string;
              url: string;
          }[]
        | "-";
    kid:
        | {
              lang: string;
              name: string;
              url: string;
          }[]
        | "-";
    dis1y: string;
    dis2y: string;
    dis3y: string;
    dis4y: string;
    dis5y: string;
    cal1y: string;
    cal2y: string;
    cal3y: string;
    cal4y: string;
    cal5y: string;
    cal6y: string;
    cal7y: string;
    cal8y: string;
    cal9y: string;
    cal10y: string;
    bidChange: string;
    bidChange__date: string;
    midChange: string;
    midChange__date: string;
    midPrice: string;
    midPrice__date: string;
    bidPrice: string;
    bidPrice__date: string;
    offerPrice: string;
    offerPrice__date: string;
    midPriceGerman: string;
    midPriceGerman__date: string;
    mtmNAV: string;
    mtmNAV__date: string;
    differenceNAV: string;
    differenceNAV__date: string;
    lvNAV: string;
    lvNAV__date: string;
    accNAV: string;
    accNAV__date: string;
    EURlvNAV: string;
    EURlvNAV__date: string;
    EURmtmNAV: string;
    EURmtmNAV__date: string;
    EURdifferenceNAV: string;
    EURdifferenceNAV__date: string;
};

export type FundMeta = {
    baseCurrency: string;
    fundPageURL: string;
    name: string;
    descriptor: string;
    benchmark: string;
    style: string;
    domicile: string;
    sector: string;
    assetClass: string;
    lgimInternalAcronym: string;
    isIncome: string;
    priceType: string;
    valuationTime: string;
    capabilityStyle: string;
    settlementPeriod: string;
    sfdrClassification: string;
    productType: string;
    brochure: string;
    reasonsWhy: string;
    investorRights: string;
    prospectus:
        | {
              lang: string;
              name: string;
              url: string;
          }[]
        | "-";
    aum: string;
    aum__date: string;
    availabilityNote: string;
    importantInformation: string;
    ukPropertyMessage: string;
    globalIBORReformsMessage: string;
    worldwideTrustMessage: string;
    worldwideTrustMessagePrivate: string;
    newUnlaunchedFundsMessage: string;
};
